import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Card, CardActionArea, CardContent, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { BeachAccess } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { employeesApi } from "../../../api/employeesApi";
import { absenceApi } from "../../../api/absenceApi";
import { EmployeeUserType } from "../../../types/employeeType";
import { AbsenceEntry, CurrentVacationQuota } from "../../../types/absence";
import { useLocale } from "../../../hooks/UseLocale";
import { usePermissionChecker } from "../../../hooks/usePermissionChecker";
import { PermissionEntityType } from "../../../types/permissionType";
import VacationQuotaDialog from "./VacationQuotaDialog";
import { companyManagementApi } from "../../../api/companyManagementApi";
import DownloadIcon from "@mui/icons-material/Download";
import {GeneralButton} from "../../../common/ui/GeneralButton";
import {useExport} from "../../../hooks/useExport";
import {ExportDialog} from "../ExportDialog";


type EmployeeAbsenceSummary = {
  employee: EmployeeUserType;
  remainingVacationDays: number | null;
  surplusFromPrevious: number | null;
  surplusValidUntil: string | null;
  illnessDays: number;
  plannedVacationDays: number;
  takenVacationDays: number;
  pendingApprovals: number;
};

const AbsenceOverviewPage = () => {
  const { t } = useTranslation();
  const { serviceModuleId } = useParams<{ serviceModuleId: string }>();
  const parsedServiceModuleId = Number(serviceModuleId);
  const locale = useLocale();
  const navigate = useNavigate();
  const { userHasPermission } = usePermissionChecker();
  const canEditEmployees = userHasPermission(PermissionEntityType.Employees, "Create");
  const [employees, setEmployees] = useState<EmployeeUserType[]>([]);
  const [summaries, setSummaries] = useState<EmployeeAbsenceSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [quotaDialogEmployee, setQuotaDialogEmployee] = useState<EmployeeUserType | null>(null);
  const [openQuotaDialog, setOpenQuotaDialog] = useState(false);

  const allEntriesPath = useMemo(() => {
    if (!Number.isFinite(parsedServiceModuleId)) return "";
    return `/${locale}/servicemodules/absence/${parsedServiceModuleId}/all`;
  }, [locale, parsedServiceModuleId]);

  useEffect(() => {
    if (!Number.isFinite(parsedServiceModuleId)) return;
    const fetchEmployees = async () => {
      const response = await employeesApi.getServiceModuleEmployees(parsedServiceModuleId);
      if (response.success() && response.result) {
        setEmployees(response.result);
      } else {
        setEmployees([]);
      }
    };
    fetchEmployees();
  }, [parsedServiceModuleId]);

  useEffect(() => {
    const fetchCompany = async () => {
      const company = await companyManagementApi.getCompany();
      if (company && typeof company.id === "number") {
        setCompanyId(company.id);
      }
    };
    fetchCompany();
  }, []);

  const countIllnessDays = useCallback((absence: AbsenceEntry) => {
    const reason = absence.absenceReason ?? "";
    if (reason !== "Illness" && reason !== "IllnessWithoutDoctorsNotice" && reason !== "ChildSick") {
      return 0;
    }
    const start = dayjs(absence.startTimestamp).startOf("day");
    const end = dayjs(absence.endTimestamp).startOf("day");
    return end.diff(start, "day") + 1;
  }, []);

  const countVacationDays = useCallback((absence: AbsenceEntry) => {
    const reason = absence.absenceReason ?? "";
    if (reason !== "Vacation" && reason !== "VacationHalfDay") {
      return 0;
    }
    const start = dayjs(absence.startTimestamp).startOf("day");
    const end = dayjs(absence.endTimestamp).startOf("day");
    const days = end.diff(start, "day") + 1;
    return reason === "VacationHalfDay" ? 0.5 * days : days;
  }, []);

  const fetchEmployeeAbsences = useCallback(async (
    employeeId: string,
    startDate: string,
    endDate: string
  ) => {
    const pageSize = 200;
    let offset = 0;
    let items: AbsenceEntry[] = [];
    let totalCount = 0;

    do {
      const result = await absenceApi.getCompanyAbsencesPaginated(
        parsedServiceModuleId,
        {
          limit: pageSize,
          offset,
          employeeUserId: employeeId,
          startDate,
          endDate,
        }
      );
      if (!result.success() || !result.result) {
        break;
      }
      items = items.concat(result.result.items);
      totalCount = result.result.totalCount;
      offset += pageSize;
    } while (items.length < totalCount);

    return items.filter((absence) => {
      const absenceEmployeeId = absence.employeeUserId ?? absence.employeeUser?.id ?? "";
      return absenceEmployeeId === employeeId;
    });
  }, [parsedServiceModuleId]);

  const formatDays = (value: number) => {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  };

  useEffect(() => {
    if (!Number.isFinite(parsedServiceModuleId) || employees.length === 0) {
      setSummaries([]);
      return;
    }

    const loadSummaries = async () => {
      setIsLoading(true);
      const startDate = dayjs().startOf("year").format("YYYY-MM-DD");
      const endDate = dayjs().endOf("year").format("YYYY-MM-DD");

      const result = await Promise.all(
        employees.map(async (employee) => {
          const [absences, quota] = await Promise.all([
            fetchEmployeeAbsences(employee.id, startDate, endDate),
            absenceApi.getCurrentVacationQuota(parsedServiceModuleId, employee.id),
          ]);

          const quotaData: CurrentVacationQuota | Record<string, unknown> | null =
            quota.success() && quota.result
              ? quota.result
              : null;
          const getNumeric = (value: unknown) => {
            if (typeof value === "number") return Number.isFinite(value) ? value : null;
            if (typeof value === "string") {
              const parsed = Number(value);
              return Number.isFinite(parsed) ? parsed : null;
            }
            return null;
          };
          const getString = (value: unknown) => (typeof value === "string" ? value : null);
          const surplusValue = getNumeric(
            (quotaData as Record<string, unknown> | null)?.surplus ??
              (quotaData as Record<string, unknown> | null)?.Surplus
          );
          const surplusFromPreviousValue = getNumeric(
            (quotaData as Record<string, unknown> | null)?.surplusFromPrevious ??
              (quotaData as Record<string, unknown> | null)?.SurplusFromPrevious
          );
          const surplusValidUntilValue =
            getString(
              (quotaData as Record<string, unknown> | null)?.surplusValidUntil ??
                (quotaData as Record<string, unknown> | null)?.SurplusValidUntil
            ) ?? null;

          let illnessDays = 0;
          let plannedVacationDays = 0;
          let takenVacationDays = 0;
          let pendingApprovals = 0;
          absences.forEach((absence) => {
            illnessDays += countIllnessDays(absence);
            if (absence.absenceApproval === "Pending") {
              pendingApprovals += 1;
            }
            const vacationDays = countVacationDays(absence);
            if (vacationDays > 0) {
              if (absence.absenceApproval === "Approved") {
                takenVacationDays += vacationDays;
              } else if (absence.absenceApproval === "Pending") {
                plannedVacationDays += vacationDays;
              }
            }
          });

          return {
            employee,
            remainingVacationDays: surplusValue,
            surplusFromPrevious: surplusFromPreviousValue,
            surplusValidUntil: surplusValidUntilValue,
            illnessDays,
            plannedVacationDays,
            takenVacationDays,
            pendingApprovals,
          };
        })
      );

      setSummaries(result);
      setIsLoading(false);
    };

    loadSummaries();
  }, [countIllnessDays, countVacationDays, employees, fetchEmployeeAbsences, parsedServiceModuleId]);

  const exportDialog = useExport(
      parsedServiceModuleId,
      employees.map(e => ({ employee: e })),
      "absences"
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Box
          sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2 }}
      >
        <Tooltip title={t("serviceModulesPage.absencePage.export.tooltip")}>
          <GeneralButton
              appVariant="primary"
              onClick={exportDialog.openAllEmployees}
              disabled={isLoading || employees.length === 0}
          >
            <DownloadIcon sx={{ mr: 1 }} />
            {t("serviceModulesPage.absencePage.export.title")}
          </GeneralButton>
        </Tooltip>

        <ExportDialog
            open={exportDialog.open}
            onClose={() => exportDialog.setOpen(false)}
            year={exportDialog.year}
            month={exportDialog.month}
            format={exportDialog.format}
            setYear={exportDialog.setYear}
            setMonth={exportDialog.setMonth}
            setFormat={exportDialog.setFormat}
            onExport={exportDialog.handleExport}
            loading={exportDialog.loading}
        />

      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardActionArea
              sx={{ height: "100%" }}
              onClick={() => {
                if (allEntriesPath) navigate(allEntriesPath);
              }}
            >
              <CardContent>
                <Typography variant="h6">
                  {t("serviceModulesPage.absencePage.allEntriesCard.title")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("serviceModulesPage.absencePage.allEntriesCard.subtitle")}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        {summaries.map((summary) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={summary.employee.id}>
            <Card sx={{ height: "100%" }}>
              <CardActionArea
                sx={{ height: "100%" }}
                onClick={() => {
                  if (!Number.isFinite(parsedServiceModuleId)) return;
                  navigate(`/${locale}/servicemodules/absence/${parsedServiceModuleId}/employee/${summary.employee.id}`);
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="subtitle1">
                      {summary.employee.firstName} {summary.employee.lastName}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Tooltip title={t("employeesPage.vacationQuota.viewTooltip")}>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          setQuotaDialogEmployee(summary.employee);
                          setOpenQuotaDialog(true);
                        }}
                      >
                        <BeachAccess fontSize="small" />
                      </IconButton>
                    </Tooltip>
                      <Tooltip title={t("serviceModulesPage.absencePage.export.tooltip")}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              exportDialog.openSingleEmployee(summary.employee.id);
                            }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {isLoading
                      ? t("serviceModulesPage.absencePage.loadingEmployees")
                      : t("serviceModulesPage.absencePage.remainingVacation", {
                          days: summary.remainingVacationDays === null
                            ? "—"
                            : formatDays(summary.remainingVacationDays),
                        })}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("serviceModulesPage.absencePage.surplus", {
                      days: summary.surplusFromPrevious === null
                        ? "—"
                        : formatDays(summary.surplusFromPrevious),
                      until: summary.surplusValidUntil
                        ? dayjs(summary.surplusValidUntil).format("DD.MM.YYYY")
                        : "—",
                    })}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("serviceModulesPage.absencePage.illnessDays", {
                      days: formatDays(summary.illnessDays),
                    })}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("serviceModulesPage.absencePage.plannedVacation", {
                      days: formatDays(summary.plannedVacationDays),
                    })}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("serviceModulesPage.absencePage.takenVacation", {
                      days: formatDays(summary.takenVacationDays),
                    })}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("serviceModulesPage.absencePage.pendingApprovals", {
                      count: summary.pendingApprovals,
                    })}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      {quotaDialogEmployee && (
        <VacationQuotaDialog
          open={openQuotaDialog}
          onClose={() => {
            setOpenQuotaDialog(false);
            setQuotaDialogEmployee(null);
          }}
          employee={quotaDialogEmployee}
          absenceServiceModuleId={parsedServiceModuleId}
          canEdit={canEditEmployees}
          companyId={companyId}
          mode="view"
        />
      )}
    </Box>
  );
};

export default AbsenceOverviewPage;
